import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { AddPickToSubmissionDoc, AddPickToPresentationSubmissionPublicDoc } from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { helpers } from '@minecraft.utils';
import { HandleUpdateSubmissionFn, HandleUpdateParams, FeedbackSectionPick } from './types';

export type UseAddPickParams = {
  isPublic: boolean;
  /** id for the submission which should have the comment added */
  submissionId: string | number;
  /** this is set only in the presentation view, in the worksheet, or session it's null */
  presentationId?: string | number;
  /** if isPublic = false, this should be the account user's name
   * if isPublic = true, this should be the name of the userName that was entered
   */
  userName?: string;
  /** list of current picks */
  pickedBy: FeedbackSectionPick[] | readonly FeedbackSectionPick[];
  /** callback to update related feedback fields on other parent query data */
  handleUpdateSubmission?: HandleUpdateSubmissionFn;
} & Pick<HandleUpdateParams, 'talentEntityId'>;

export const usePickAdd = ({
  handleUpdateSubmission,
  isPublic,
  pickedBy,
  presentationId,
  submissionId,
  talentEntityId,
  userName,
}: UseAddPickParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const [mutationLoading, setMutationLoading] = useState(false);

  const [addPickToSubmission] = useMutation(AddPickToSubmissionDoc);
  const [addPickToPresentationSubmissionPublic] = useMutation(AddPickToPresentationSubmissionPublicDoc);

  const onPickAdd = useCallback(async (): Promise<void> => {
    setMutationLoading(true);
    try {
      let response;
      if (!isPublic) {
        response = await addPickToSubmission({
          variables: {
            submissionId,
            presentationId: presentationId ? Number(presentationId) : null,
          },
        });
      } else {
        response = await addPickToPresentationSubmissionPublic({
          variables: {
            submissionId: talentEntityId || submissionId,
            userName,
            presentationId: Number(presentationId),
          },
        });
      }

      if (response?.errors?.length) {
        throw response.errors[0];
      }

      if (typeof handleUpdateSubmission === 'function') {
        const addedPickId = response?.data?.addedPickId;
        const addedPickObj = {
          id: addedPickId,
          userName,
          created: new Date().toISOString(),
        };
        handleUpdateSubmission({
          talentEntityId,
          addedPick: addedPickObj,
          pickedBy: [addedPickObj, ...pickedBy],
        });
      }
    } catch (errorGraphQL) {
      captureExceptionEvent(errorGraphQL, {
        location: 'useAddPick',
        userName,
        submissionId,
        presentationId,
        isPublic: isPublic?.toString(),
      });

      const errorGraphQLName = helpers.getErrorMessage(errorGraphQL);
      let message = t('common:globalError.unexpectedServer');
      if (errorGraphQLName === 'validationError') {
        message = t('common:toast.pick.alreadyPicked');
      }

      showMessage({
        message,
        color: 'danger',
        sticky: true,
      });
    }
    setMutationLoading(false);
  }, [
    addPickToPresentationSubmissionPublic,
    addPickToSubmission,
    handleUpdateSubmission,
    isPublic,
    pickedBy,
    presentationId,
    showMessage,
    submissionId,
    t,
    talentEntityId,
    userName,
  ]);

  return useMemo(() => {
    return {
      onPickAdd,
      addPickSubmitting: mutationLoading,
    };
  }, [onPickAdd, mutationLoading]);
};
